<template>
  <section
    id="batiments"
    class="buildings"
  >
    <div class="buildings__head container">
      <p class="section-kicker">{{ data.buildings.kicker }}</p>
      <h2 class="section-title">{{ data.buildings.title }}</h2>
      <img
        class="buildings__stars"
        src="/assets/pictos/decor-buildings.svg"
        alt=""
        aria-hidden="true"
      >
    </div>

    <!-- Carte sticky à gauche, le pin actif change au scroll (réf. nesmapartners.com) -->
    <div class="buildings__scroll container">
      <div class="buildings__map-col">
        <div class="buildings__map">
          <img
            v-for="(building, i) in data.buildings.items"
            :key="building.id"
            :src="building.map"
            :alt="`Plan du campus - ${building.name}`"
            class="buildings__map-layer"
            :class="{ 'is-active': i === activeIndex }"
            :loading="i === 0 ? 'eager' : 'lazy'"
          >
        </div>
      </div>

      <div
        ref="cardsEl"
        class="buildings__cards"
        @scroll.passive="onCardsScroll"
      >
        <article
          v-for="(building, i) in data.buildings.items"
          :key="building.id"
          :ref="setCardRef"
          class="building-card"
          :data-index="i"
        >
          <h3 class="building-card__title">{{ building.name }}</h3>

          <div
            v-if="hasTabs(building)"
            class="building-card__tabs"
          >
            <button
              v-for="(tab, ti) in building.tabs"
              :key="tab.label"
              class="building-card__tab"
              :class="{ 'is-active': ti === tabIndexFor(i) }"
              @click="setTab(i, ti)"
            >
              <span class="building-card__tab-arrow">→</span> {{ tab.label }}
            </button>
          </div>

          <div class="building-card__media">
            <img
              :key="currentTab(building, i).image"
              :src="currentTab(building, i).image"
              :alt="`${building.name} - ${currentTab(building, i).label || 'photo'}`"
              class="building-card__photo"
            >
            <p
              v-if="currentTab(building, i).caption"
              class="building-card__caption"
            >{{ currentTab(building, i).caption }}</p>
          </div>

          <ul
            v-if="building.features"
            class="building-card__features"
          >
            <li
              v-for="f in building.features"
              :key="f.label"
            >
              <span class="building-card__feature-icon">
                <img
                  :src="f.icon"
                  alt=""
                >
              </span>
              <span>{{ f.label }}</span>
            </li>
          </ul>
        </article>
      </div>
    </div>

    <div class="buildings__banner-clip">
      <MarqueeBand
        class="buildings__banner"
        :text="data.buildings.banner"
        color="red"
      />
    </div>
  </section>
</template>

<script>
import MarqueeBand from '@/components/MarqueeBand.vue'

export default {
  name: 'BuildingsSection',
  components: { MarqueeBand },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeIndex: 0,
      tabIndexes: {},
      cardEls: [],
      observer: null
    }
  },
  beforeUpdate() {
    this.cardEls = []
  },
  mounted() {
    // La carte au centre du viewport devient active, le plan bascule sur son calque.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.isHorizontal()) return
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeIndex = Number(entry.target.dataset.index)
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    )
    this.cardEls.forEach((el) => this.observer.observe(el))
    this.onCardsScroll()
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    setCardRef(el) {
      if (el) this.cardEls.push(el)
    },
    // Mobile : le calque actif suit la carte la plus proche du centre du carrousel.
    isHorizontal() {
      const el = this.$refs.cardsEl
      return !!el && el.scrollWidth > el.clientWidth + 8
    },
    onCardsScroll() {
      const el = this.$refs.cardsEl
      if (!el || !this.isHorizontal()) return
      const center = el.scrollLeft + el.clientWidth / 2
      let best = 0
      let bestDist = Infinity
      this.cardEls.forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2
        const dist = Math.abs(cardCenter - center)
        if (dist < bestDist) {
          bestDist = dist
          best = Number(card.dataset.index)
        }
      })
      this.activeIndex = best
    },
    hasTabs(building) {
      return building.tabs.length > 1 || building.tabs[0].label
    },
    tabIndexFor(cardIndex) {
      return this.tabIndexes[cardIndex] || 0
    },
    setTab(cardIndex, tabIndex) {
      this.tabIndexes = { ...this.tabIndexes, [cardIndex]: tabIndex }
    },
    currentTab(building, cardIndex) {
      return building.tabs[this.tabIndexFor(cardIndex)] || building.tabs[0]
    }
  }
}
</script>

<style lang="scss" scoped>
.buildings {
  background: $beige;
  color: $navy;
  padding-top: 96px;
  // largeur de container propre à la section : élargie sur grand écran pour que
  // le menu d'onglets des cartes tienne sur une seule ligne
  --bw: min(1200px, 100vw - 44px);
  // de combien le plan passe sous la colonne de cartes
  --map-overlap: clamp(48px, 6vw, 140px);
  // pas d'overflow ici : il casserait le position: sticky du plan

  @media (min-width: 1500px) {
    --bw: min(1400px, 100vw - 44px);
  }
}

.buildings__head,
.buildings__scroll {
  width: var(--bw);
}

.buildings__head {
  position: relative;
}

// décor : collé au coin haut-droit du viewport, grand écran uniquement
.buildings__stars {
  display: none;
  position: absolute;
  top: -96px;
  right: calc((var(--bw) - 100vw) / 2);
  width: 140px;
  pointer-events: none;
  user-select: none;

  @media (min-width: 1400px) {
    display: block;
  }
}

.buildings__scroll {
  display: grid;
  grid-template-columns: minmax(0, 2.1fr) minmax(0, 1fr);
  gap: clamp(16px, 2vw, 30px);
  align-items: start;
}

// le plan déborde du container à gauche et passe sous la colonne de cartes
.buildings__map-col {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: calc((var(--bw) - 100vw) / 2);
}

.buildings__map {
  position: relative;
  flex: 0 0 auto;
  // borné par la hauteur du viewport : sinon le plan déborde en haut de sa
  // colonne sticky et passe par-dessus le titre sur les écrans peu hauts
  width: min(104%, 100vh);
  // le bord droit du plan glisse sous les cartes blanches
  margin-right: calc(-1 * var(--map-overlap));
  margin-bottom: -8vh;
  aspect-ratio: 792 / 680.2; // recadrage du SVG

  &-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.45s ease;

    &.is-active {
      opacity: 1;
    }
  }
}

.buildings__cards {
  display: flex;
  flex-direction: column;
  gap: 48px;
  // peu de retrait en haut (la première carte démarre près du titre),
  // beaucoup en bas pour laisser le plan sticky défiler jusqu'au dernier bâtiment
  padding: 4vh 0 18vh;
  // au-dessus du plan sticky qui déborde sous la colonne
  position: relative;
  z-index: 1;
}

.building-card {
  background: $white;
  border-radius: 22px;
  padding: 30px 30px 26px;
  box-shadow: 0 14px 40px rgba($navy, 0.08);
}

.building-card__title {
  font-size: 26px;
  font-weight: 700;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba($navy, 0.2);
}

.building-card__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 12px 0 14px;
  border-bottom: 1px solid rgba($navy, 0.12);
  margin-bottom: 18px;
}

.building-card__tab {
  background: none;
  border: none;
  padding: 2px 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $navy;
  opacity: 0.65;
  transition: color 0.2s ease, opacity 0.2s ease;

  &-arrow {
    color: $red;
  }

  &.is-active,
  &:hover {
    color: $red;
    opacity: 1;
  }
}

.building-card__media {
  position: relative;

  img {
    width: 100%;
    aspect-ratio: 4 / 4.6;
    object-fit: cover;
  }

  // effet bleu en bas de chaque photo (maquette), présent même sans légende
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 42%;
    pointer-events: none;
    background: linear-gradient(180deg, rgba($navy, 0) 0%, rgba($navy, 0.5) 45%, rgba($navy, 0.9) 100%);
  }
}

.building-card__caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 16px 18px 16px;
  font-size: 13.5px;
  font-weight: 500;
  color: $white;
}

.building-card__features {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 18px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
  }

  // la dernière puce a un libellé long -> police réduite pour qu'il tienne mieux
  li:last-child {
    font-size: 11px;
    line-height: 1.3;
  }
}

.building-card__feature-icon {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: $yellow;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 44px;
    height: 44px;
  }
}

// fondu au changement d'onglet (animation au montage, robuste même en onglet caché)
.building-card__photo {
  animation: tabfade-in 0.3s ease;
}

@keyframes tabfade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

// le bandeau chevauche la jonction beige/navy pour une transition sans trou de couleur
.buildings__banner-clip {
  overflow: hidden;
  position: relative;
  z-index: 2;
  // fond incliné au même angle que le bandeau : beige au-dessus, navy en dessous.
  // Sans lui, la section navy (remontée sous le clip) apparaît aux extrémités que
  // le bandeau incliné ne couvre pas (visible surtout sur grand écran).
  background: linear-gradient(178.9deg, $beige 0 50%, $navy 50% 100%);
  // le padding doit absorber le débord dû à l'inclinaison (largeur x tan 1,1°),
  // sinon overflow: hidden rogne la bande et équerre ses extrémités
  padding: calc(10px + 0.96vw) 0;
  margin-top: -8px;
  // moitié de la hauteur du clip (bande ~50px + padding)
  margin-bottom: calc(-35px - 0.96vw);
}

@include tablet-down {
  .buildings__scroll {
    display: block;
  }

  .buildings__map-col {
    position: relative;
    height: auto;
    margin-inline: -22px;
    // pas de colonne de cartes à droite en mobile : on repart du bord gauche
    justify-content: flex-start;
  }

  // plan agrandi en mobile (débordait trop petit) : zoom + léger débord latéral.
  // La marge négative fait remonter les cartes sur le bas du plan, qui est vide
  // à droite (l'illustration est en diagonale).
  .buildings__map {
    width: 146%;
    margin-left: -23%;
    margin-right: 0;
    margin-bottom: -22vw;
    aspect-ratio: 792 / 680.2; // recadrage du SVG
    height: auto;

    &-layer {
      position: absolute;
      object-fit: contain;
    }
  }

  // maquette mobile : image, puis titre, puis la liste des espaces
  .building-card {
    display: flex;
    flex-direction: column;
  }

  .building-card__media { order: 1; }

  .building-card__title {
    order: 2;
    border-bottom: 0;
    padding-bottom: 0;
    margin-top: 16px;
  }

  .building-card__tabs {
    order: 3;
    border-top: 1px solid rgba($navy, 0.15);
    border-bottom: 1px solid rgba($navy, 0.15);
    padding: 12px 0;
    margin: 14px 0 0;
    gap: 6px 14px;
  }

  // les 6 pictos parkings alourdissent la carte en mobile
  .building-card__features {
    display: none;
  }

  // cartes en carrousel horizontal (maquette mobile)
  .buildings__cards {
    flex-direction: row;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 16px;
    padding: 26px 22px 40px;
    margin-inline: -22px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .building-card {
    flex: 0 0 84%;
    scroll-snap-align: center;
    max-width: 380px;
  }
}
</style>
